import { DebugBox, LoadingBox, useNavigateWithParams } from "@northpowerdev/np-react-utils";
import { useEffect, useState } from "react";
import { Box, Button, Card, Stack } from "@mui/material";
import useFetchJson from "../../../data/useFetchJson";
import { DlspTaskDetailsPanel } from "./DlspTaskDetailsPanel";
import { DlspBreadcrumbs } from "./DlspBreadcrumbs";
import { useGetParams } from "../../../utils/useGetParams";
import DlspTaskStatusChip from "../../styled/DlspTaskStatusChip";
import { DlspDesktopAssessmentPanel } from "./DlspDesktopAssessmentPanel";
import { DlspDesktopFileUploadPanel } from "./DlspDesktopFileUploadPanel";
import { DlspDesktopQAPanel } from "./DlspDesktopQAPanel";
import { DlspTaskDetailsQAOwnershipDialog } from "./DlspTaskDetailsQAOwnershipDialog";
import { MahiContext, mahiNameFromMahiContext, mahiRolesFromMahiContext } from "../../state/MahiContext";
import { useContext } from "react";
import { DlspFieldInspectionPanel } from "./DlspFieldInspectionPanel";
import { DlspLandOwnerPermissionPanel } from "./DlspLandOwerPermissionPanel";
import { DlspTitleTypography } from "../../styled/DlspTitleTypography";
import { DlspFieldQAPanel } from "./DlspFieldQAPanel";
import { DlspRemedialAssessmentPanel } from "./DlspRemedialAssessmentPanel";

export default function DlspTaskDetails() {

    const [desktopEditCounter, setDesktopEditCounter] = useState(0)
    const getParams = useGetParams(['icp_number', 'assignee'])
    const [qaOwnershipDialogOpen, setQAOwnershipDialogOpen] = useState(false)
    const [userName, setUserName] = useState()
    const [isQARole, setIsQARole] = useState(false)
    const [fspStatusNoEdit, setFspStatusNoEdit] = useState(false)

    const mahiContext = useContext(MahiContext)

    const getTaskDetailsPath = '/bff/decommissioned-lines-service/api/decommissioned-lines/get-detail'
    const {
        isLoading: taskDetailsLoading,
        data: taskDetails, // E.g. { decomICP, odsICP, gsICP, odsDSub, statusHistory }
        load: getTaskDetails } = useFetchJson(getTaskDetailsPath, {}, false)

    const claimDesktopQAPath = '/bff/decommissioned-lines-service/api/decommissioned-lines/claim-desktop-qa-ownership'
    const { load: claimDesktopQA } = useFetchJson(claimDesktopQAPath, { method: 'POST' })

    const navigateToFieldInspection = useNavigateWithParams('/dlsp/task/field_inspection')

    useEffect(() => {
        if (!mahiContext) {
            return
        }
        const mahiRoles = mahiRolesFromMahiContext(mahiContext)
        setIsQARole(mahiRoles.includes('Mahi.DecommissionedLines.QA'))
        const mahiName = mahiNameFromMahiContext(mahiContext)
        setUserName(mahiName)
    }, [mahiContext])

    useEffect(() => {
        // console.log('DlspTaskDetails useEffect got getParams', getParams)
        if (!getParams?.icp_number) {
            return
        }
        const taskListParams = {
            params: {
                icpNo: getParams?.icp_number,
            }
        }
        getTaskDetails(taskListParams, getTaskDetailsPath)

    }, [getParams, getTaskDetails, desktopEditCounter])

    useEffect(() => {
        if (!taskDetails) return
        const isDesktopQAStatus = taskDetails?.decomICP?.status === 'Desktop QA'
        const isNoDesktopQAOwner = !taskDetails?.decomICP?.desktopQAOwner
        setFspStatusNoEdit(taskDetails?.decomICP?.status === 'FSP' ? true : false)
        if (isQARole && isDesktopQAStatus && isNoDesktopQAOwner) {
            setQAOwnershipDialogOpen(true)
        }
    }, [taskDetails, isQARole])

    const handleDesktopAssessmentEdited = () => {
        setDesktopEditCounter(c => c + 1) // trigger useEffect
    }

    const handleDesktopQAEdited = () => {
        setDesktopEditCounter(c => c + 1) // trigger useEffect
    }

    const handleLandOwnerPermissionEdited = () => {
        setDesktopEditCounter(c => c + 1) // trigger useEffect
    }

    const handleFieldQAEdited = () => {
        setDesktopEditCounter(c => c + 1) // trigger useEffect
    }

    const handleClaimQAOwnership = async () => {
        console.log('claim ownership')
        const fetchParams = { params: { icp: taskDetails.decomICP.icp } }
        const claimSucceeded = await claimDesktopQA(fetchParams)
        if (claimSucceeded !== undefined) {
            // success - ownership was claimed
            setQAOwnershipDialogOpen(false)
            setDesktopEditCounter(c => c + 1) // trigger useEffect
        }
    }

    const createEditFieldInspectionText = taskDetails?.decomICP?.fieldChecklist ? "Edit Field Inspection" : "Create Field Inspection"

    const handleFieldInspectionClick = () => {
        const navigateParams = {
            icp_number: getParams?.icp_number,
        }
        if (getParams?.assignee) {
            navigateParams.assignee = getParams?.assignee

        }
        navigateToFieldInspection(navigateParams)
    }

    const readOnlyStyling = {
        position: 'relative',
        '&:before': {
            margin: '3rem',
            content: '"Read Only"',
            position: 'absolute',
            transform: 'rotate(45deg)',
            color: 'rgba(0, 0, 0, 0.1)',
            top: '50%',
            left: '50%',
            mt:' -50px',
            ml:'-100px',
            fontSize: '3rem'
        }
    }

    return (
        <Box className="Page" backgroundColor={fspStatusNoEdit ? 'rgba(0, 0, 0, 0.05)' : ''}>
            <Stack direction="row" sx={{ flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <h3>Decommissioned Lines Safety Project</h3>
                    <h2>Task at {taskDetails?.decomICP?.address}</h2>
                </Box>
                <DlspTaskStatusChip status={taskDetails?.decomICP?.status} statusReason={taskDetails?.decomICP?.assignee} />
            </Stack>

            <Stack direction="row" sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                <DlspBreadcrumbs
                    assignee={getParams?.assignee}
                    stormArea={taskDetails?.decomICP?.stormArea}
                    suburb={taskDetails?.odsDSub?.suburb}
                    taskAddress={taskDetails?.decomICP?.address}
                />
                {
                    ["Field", "Field QA"].includes(taskDetails?.decomICP?.status)
                    &&
                    <Button variant="contained" onClick={handleFieldInspectionClick}>{createEditFieldInspectionText}</Button>
                }
            </Stack>

            <LoadingBox loading={taskDetailsLoading}>
                <Box>
                    <DlspTaskDetailsPanel taskDetails={taskDetails} fspStatusNoEdit={fspStatusNoEdit} readOnlyStyling={readOnlyStyling} />
                    <Card sx={{ p: 1, mb: 4, backgroundColor: fspStatusNoEdit ? 'rgba(0, 0, 0, 0.05)' : '' }} >
                        <DlspTitleTypography variant="h4" color="secondary">Desktop Photos & Screenshots</DlspTitleTypography>
                        <DlspDesktopFileUploadPanel icp={taskDetails?.decomICP?.icp} fspStatusNoEdit={fspStatusNoEdit} />
                    </Card>

                    <Card sx={{ p: 1, mb: 4, position: 'relative', backgroundColor: fspStatusNoEdit ? 'rgba(0, 0, 0, 0.05)' : '', ...(fspStatusNoEdit && readOnlyStyling) }}>
                        <DlspTitleTypography variant="h4" color="secondary" >Desktop Assessment</DlspTitleTypography>
                        <DlspDesktopAssessmentPanel
                            taskDetails={taskDetails}
                            assignee={getParams?.assignee}
                            onEdited={handleDesktopAssessmentEdited}
                        />
                    </Card>

                    <Card sx={{ p: 1, mb: 4, backgroundColor: fspStatusNoEdit ? 'rgba(0, 0, 0, 0.05)' : '', ...(fspStatusNoEdit && readOnlyStyling) }}>
                        {/* {fspStatusNoEdit && <Typography variant="h1" color={'rgba(0, 0, 0, 0.1)'} sx={readOnlyStyling}>Read only</Typography>} */}
                        <DlspTitleTypography variant="h4" color="secondary">Desktop QA</DlspTitleTypography>
                        <DlspDesktopQAPanel
                            taskDetails={taskDetails}
                            assignee={getParams?.assignee}
                            onEdited={handleDesktopQAEdited}
                            userName={userName}
                            isQARole={isQARole}
                            fspStatusNoEdit={fspStatusNoEdit}
                        />
                    </Card>

                    <DlspTaskDetailsQAOwnershipDialog
                        open={qaOwnershipDialogOpen}
                        onClaimOwnership={handleClaimQAOwnership}
                        onClose={() => setQAOwnershipDialogOpen(false)}
                    />
                    {/* Mick Temp: disabled landOwnerPermissionRequired */}
                    {
                        (taskDetails?.decomICP?.desktopQA?.landOwnerPermissionRequired === 'Yes' /* && false */)
                        &&
                        <Card sx={{ p: 1, mb: 4, backgroundColor: fspStatusNoEdit ? 'rgba(0, 0, 0, 0.05)' : '', ...(fspStatusNoEdit && readOnlyStyling) }}>
                            <DlspTitleTypography variant="h4" color="secondary" paragraph>Land Owner Permission</DlspTitleTypography>
                            <DlspLandOwnerPermissionPanel
                                taskDetails={taskDetails}
                                onEdited={handleLandOwnerPermissionEdited}
                                fspStatusNoEdit={fspStatusNoEdit}
                            />
                        </Card>
                    }

                    {
                        (taskDetails?.decomICP?.fieldChecklist || ["Field"].includes(taskDetails?.decomICP?.status))
                        &&
                        <Card sx={{ p: 1, mb: 4, backgroundColor: fspStatusNoEdit ? 'rgba(0, 0, 0, 0.05)' : '', ...(fspStatusNoEdit && readOnlyStyling) }}>
                            {/* {fspStatusNoEdit && <Typography variant="h1" color={'rgba(0, 0, 0, 0.1)'} sx={readOnlyStyling}>Read only</Typography>} */}
                            <DlspTitleTypography variant="h4" color="secondary">Field Inspection</DlspTitleTypography>
                            <DlspFieldInspectionPanel taskDetails={taskDetails} />
                            {
                                ["Field", "Field QA"].includes(taskDetails?.decomICP?.status)
                                &&
                                <Button variant="contained" onClick={handleFieldInspectionClick}>{createEditFieldInspectionText}</Button>
                            }
                        </Card>
                    }

                    {
                        (taskDetails?.decomICP?.fieldChecklist) // We've done a field inspection, so show the field QA panel/form
                        &&
                        <Card sx={{ p: 1, mb: 4, backgroundColor: fspStatusNoEdit ? 'rgba(0, 0, 0, 0.05)' : '', ...(fspStatusNoEdit && readOnlyStyling) }}>
                            {/* {fspStatusNoEdit && <Typography variant="h1" color={'rgba(0, 0, 0, 0.1)'} sx={readOnlyStyling}>Read only</Typography>} */}
                            <DlspTitleTypography variant="h4" color="secondary">Field QA</DlspTitleTypography>
                            <DlspFieldQAPanel
                                taskDetails={taskDetails}
                                assignee={getParams?.assignee}
                                onEdited={handleFieldQAEdited}
                                userName={userName}
                                isQARole={isQARole}
                                fspStatusNoEdit={fspStatusNoEdit}
                            />
                        </Card>
                    }
                    {
                        (taskDetails?.decomICP.status === 'Remedial' || taskDetails?.decomICP.remedialBy)
                        &&
                        <Card sx={{ p: 1, mb: 4, backgroundColor: fspStatusNoEdit ? 'rgba(0, 0, 0, 0.05)' : '', ...(fspStatusNoEdit && readOnlyStyling) }}>
                            {/* {fspStatusNoEdit && <Typography variant="h1" color={'rgba(0, 0, 0, 0.1)'} sx={readOnlyStyling}>Read only</Typography>} */}
                            <DlspTitleTypography variant="h4" color="secondary">Remedial - FSP Instructions</DlspTitleTypography>
                            <DlspRemedialAssessmentPanel
                                taskDetails={taskDetails}
                                assignee={getParams?.assignee}
                                onEdited={handleFieldQAEdited}
                                userName={userName}
                                isQARole={isQARole}
                                fspStatusNoEdit={fspStatusNoEdit}
                            />
                        </Card>
                    }

                    <DebugBox title="taskDetails" stringify>{taskDetails}</DebugBox>
                </Box>
            </LoadingBox>
        </Box>
    )
}

